import React, { useState, useEffect } from 'react';
import { useHRMS } from '../context/HRMSContext.jsx';
import api from '../services/api.js';
import DatePicker from '../components/DatePicker.jsx';
import Message from '../components/Message.jsx';
import PermissionGuard from "../components/PermissionGuard.jsx";
import 'bootstrap-icons/font/bootstrap-icons.css';
import { MODULES, ACTIONS } from "../context/PermissionsContext.jsx";
function Leave() {
  const { state, loadLeaves } = useHRMS();
  const [showModal, setShowModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [leaveData, setLeaveData] = useState({
    worker_id: '', type: '', startDate: '', endDate: '', session: '', reason: ''
  });
  const [message, setMessage] = useState({ text: '', type: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check required fields
    if (!leaveData.startDate) {
      setMessage({ text: 'From date is required!', type: 'error' });
      return;
    }
    
    // Validation conditions
    const startDate = new Date(leaveData.startDate);
    const endDate = leaveData.endDate ? new Date(leaveData.endDate) : new Date(leaveData.startDate);
    const today = new Date();
    
    if (startDate < today.setHours(0,0,0,0)) {
      setMessage({ text: 'Start date cannot be in the past!', type: 'error' });
      return;
    }
    
    if (leaveData.endDate && endDate < startDate) {
      setMessage({ text: 'End date should be same or after start date!', type: 'error' });
      return;
    }
    
    const leaveDays = calculateDays(leaveData.startDate, leaveData.endDate || leaveData.startDate);
    const selectedLeaveType = (state.leaveTypes || []).find(lt => lt.leaveName === leaveData.type);
    
    let experienceWarning = '';
    let maxDays = selectedLeaveType ? selectedLeaveType.maxPerYear : 30;
    
    // Check experience eligibility if leave type is experience-based
    if (selectedLeaveType && selectedLeaveType.byExperience) {
      const experienceCheck = checkWorkerExperience(leaveData.worker_id, selectedLeaveType);
      if (!experienceCheck.eligible) {
        experienceWarning = `Note: You have ${experienceCheck.experienceYears} years experience but this leave type requires ${experienceCheck.requiredRange}. `;
      } else {
        maxDays = experienceCheck.maxPerYear || maxDays;
      }
    }
    
    // Show warning if exceeding limit
    if (leaveDays > maxDays) {
      setMessage({ 
        text: `Warning: You are applying for ${leaveDays} days but the limit for ${leaveData.type} is ${maxDays} days per year. ${experienceWarning}Application will be submitted for admin review.`, 
        type: 'warning' 
      });
    }
    
    // Show experience warning
    if (experienceWarning && leaveDays <= maxDays) {
      setMessage({ 
        text: `Warning: ${experienceWarning}Application will be submitted for admin review.`, 
        type: 'warning' 
      });
    }
    
    if (leaveData.reason.length < 1) {
      setMessage({ text: 'Please provide a detailed reason (minimum 1 characters)', type: 'error' });         
      return;
    }
    
    const existingLeaveSameDay = (state.leaves || []).find(leave => 
  leave.workerId === leaveData.worker_id &&
  new Date(leave.startDt).toDateString() === new Date(leaveData.startDate).toDateString()
);

if (existingLeaveSameDay) {
  setMessage({
    text: "Leave already applied for this date!",
    type: "error"
  });
  return;
}

try {
  const leavePayload = {
    workerId: leaveData.worker_id,
    graceperiodId: state.gracePeriods && state.gracePeriods.length > 0 ? state.gracePeriods[0].id : null,
    type: leaveData.type,
    startDt: leaveData.startDate,
    endDt: leaveData.endDate || leaveData.startDate,
    session: leaveData.session,
    reason: leaveData.reason,
    leaveStatus: 0
  };
      
      await api.leaves.create(leavePayload);
      setMessage({ text: 'Leave application submitted successfully!', type: 'success' });
      loadLeaves();
      setLeaveData({ worker_id: '', type: '', startDate: '', endDate: '', session: '', reason: '' });
      setShowModal(false);
    } catch (error) {
      console.error('Error creating leave:', error);
      setMessage({ text: 'Error submitting leave application!', type: 'error' });
    }
  };

  const checkWorkerExperience = (worker_id, leaveType) => {
    const worker = (state.workers || []).find(w => w.id === worker_id);
    if (!worker || !leaveType.byExperience) return { eligible: true };
    
    // Use joinDate from worker object (could be joinDt or joinDate)
    const joinDate = new Date(worker.joinDate || worker.joinDt);
    if (!joinDate || isNaN(joinDate.getTime())) return { eligible: true };
    
    const today = new Date();
    const experienceYears = (today - joinDate) / (1000 * 60 * 60 * 24 * 365.25);
    
    // Handle multiple experience ranges
    if (leaveType.experienceRanges && leaveType.experienceRanges.length > 0) {
      const applicableRange = leaveType.experienceRanges.find(range => {
        const fromYears = range.experienceFrom || 0;
        const toYears = range.experienceTo;
        return experienceYears >= fromYears && (toYears ? experienceYears <= toYears : true);
      });
      
      if (applicableRange) {
        return {
          eligible: true,
          experienceYears: Math.floor(experienceYears * 10) / 10,
          requiredRange: applicableRange.experienceTo ? `${applicableRange.experienceFrom}-${applicableRange.experienceTo} years` : `${applicableRange.experienceFrom}+ years`,
          maxPerYear: applicableRange.experienceMaxPerYear
        };
      } else {
        // Find the closest range for error message
        const sortedRanges = leaveType.experienceRanges.sort((a, b) => (a.experienceFrom || 0) - (b.experienceFrom || 0));
        const firstRange = sortedRanges[0];
        return {
          eligible: false,
          experienceYears: Math.floor(experienceYears * 10) / 10,
          requiredRange: firstRange.experienceTo ? `${firstRange.experienceFrom}-${firstRange.experienceTo} years` : `${firstRange.experienceFrom}+ years`,
          maxPerYear: 0
        };
      }
    } else {
      // Fallback to old single range format
      const fromYears = leaveType.experienceFrom || 0;
      const toYears = leaveType.experienceTo || 999;
      const eligible = experienceYears >= fromYears && (leaveType.experienceTo ? experienceYears <= toYears : true);
      
      return {
        eligible,
        experienceYears: Math.floor(experienceYears * 10) / 10,
        requiredRange: leaveType.experienceTo ? `${fromYears}-${toYears} years` : `${fromYears}+ years`,
        maxPerYear: leaveType.experienceMaxPerYear || leaveType.maxPerYear
      };
    }
  };

  const handleStatusChange = async (leaveId, newStatus) => {
    try {
      if (newStatus === 'Approved') {
        const leaveToApprove = (state.leaves || []).find(leave => leave.id === leaveId);
        if (leaveToApprove) {
          const selectedLeaveType = (state.leaveTypes || []).find(lt => lt.leaveName === leaveToApprove.type);
          
          if (selectedLeaveType) {
            // Check experience eligibility
            const experienceCheck = checkWorkerExperience(leaveToApprove.workerId, selectedLeaveType);
            
            if (!experienceCheck.eligible) {
              setMessage({ 
                text: `Warning: Worker has ${experienceCheck.experienceYears} years of experience but this leave type requires ${experienceCheck.requiredRange} experience. Leave approved with admin override.`, 
                type: 'warning' 
              });
            }
            
            // Check leave limit and show warning
            const maxDays = experienceCheck.maxPerYear || selectedLeaveType.maxPerYear || 30;
            const workerLeaveBreakdown = getLeaveTypeBreakdown(leaveToApprove.workerId);
            const currentTypeLeaves = workerLeaveBreakdown[leaveToApprove.type] || 0;
            const leaveDays = calculateDays(leaveToApprove.startDt, leaveToApprove.endDt);
            
            if (currentTypeLeaves + leaveDays > maxDays) {
              setMessage({ 
                text: `Warning: Worker has already taken ${currentTypeLeaves} days for ${leaveToApprove.type} leave type. This approval will exceed the limit of ${maxDays} days per year by ${(currentTypeLeaves + leaveDays) - maxDays} days. Leave approved with admin override.`, 
                type: 'warning' 
              });
            }
          }
        }
      }
      
      const statusCode = newStatus === 'Approved' ? 1 : 2;
      await api.leaves.updateStatus(leaveId, statusCode);
      loadLeaves();
      setMessage({ text: `Leave ${newStatus.toLowerCase()} successfully!`, type: 'success' });
    } catch (error) {
      console.error('Error updating leave status:', error);
      setMessage({ text: 'Error updating leave status!', type: 'error' });
    }
  };

  const getWorkerName = (leave) => {
    // First try to get from association
    if (leave.worker && leave.worker.name) {
      return leave.worker.name;
    }
    // Fallback to finding in workers array using workerId
    if (state.workers && Array.isArray(state.workers)) {
      const worker = state.workers.find(worker => worker.id === leave.workerId);
      return worker ? worker.name : 'Unknown';
    }
    return 'Unknown';   
  };

  const getTotalPreviousLeaves = (worker_id) => {
    if (!state.leaves || !Array.isArray(state.leaves)) return 0;
    return state.leaves.filter(leave => 
      leave.workerId === worker_id && leave.leaveStatus === 1 // Only approved leaves
    ).reduce((total, leave) => {
      if (leave.session === 'First Half' || leave.session === 'Second Half') {
        return total + 0.5;
      }
      return total + calculateDays(leave.startDt, leave.endDt);
    }, 0);
  };

  const getLeaveTypeBreakdown = (worker_id) => {
    if (!state.leaves || !Array.isArray(state.leaves)) return {};
    
    const approvedLeaves = state.leaves.filter(leave => 
      leave.workerId === worker_id && leave.leaveStatus === 1
    );
    
    const breakdown = {};
    approvedLeaves.forEach(leave => {
      const leaveType = leave.type;
      const days = (leave.session === 'First Half' || leave.session === 'Second Half') 
        ? 0.5 
        : calculateDays(leave.startDt, leave.endDt);
      
      if (breakdown[leaveType]) {
        breakdown[leaveType] += days;
      } else {
        breakdown[leaveType] = days;
      }
    });
    
    return breakdown;
  };

  const formatLeaveBreakdown = (worker_id) => {
    const breakdown = getLeaveTypeBreakdown(worker_id);
    const entries = Object.entries(breakdown);
    
    if (entries.length === 0) return 'No leaves taken';
    
    return entries.map(([type, days]) => `${type}: ${days}`).join(', ');
  };

  const calculateDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };



  // Handle browser back button to close modals
  useEffect(() => {
    const handlePopState = (event) => {
      if (showModal || showInfoModal) {
        event.preventDefault();
        event.stopPropagation();
        setShowModal(false);
        setShowInfoModal(false);
        setSelectedLeave(null);
        setLeaveData({ worker_id: '', type: '', startDate: '', endDate: '', session: '', reason: '' });
      }
    };

    if (showModal || showInfoModal) {
      window.history.pushState({ page: 'leave', modalOpen: true }, '', '#leave-modal');
      window.addEventListener('popstate', handlePopState, true);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState, true);
    };
  }, [showModal, showInfoModal]);

  return (
    <PermissionGuard module={MODULES.LEAVE} action={ACTIONS.VIEW} submodule={MODULES.LEAVE.submodules.LEAVE_APPLICATIONS}>
      <div className="leave">
      {message.text && (
        <Message 
          message={message.text} 
          type={message.type} 
          onClose={() => setMessage({ text: '', type: '' })} 
        />
      )}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Leave Management</h2>
        <PermissionGuard module={MODULES.LEAVE} action={ACTIONS.CREATE} submodule={MODULES.LEAVE.submodules.LEAVE_APPLICATIONS}>
          <button className="btn btn-primary" onClick={() => {
            setLeaveData({ worker_id: '', type: '', startDate: '', endDate: '', session: '', reason: '' });
            setShowModal(true);
          }}>
            Apply Leave
          </button>
        </PermissionGuard>
      </div>

      {showModal && (
        <div className="modal" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">Apply for Leave</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => {
                  setLeaveData({ worker_id: '', type: '', startDate: '', endDate: '', session: '', reason: '' });
                  setShowModal(false);
                }}></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body p-4">
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label fw-bold">Worker Name *</label>
                      <select className="form-select" value={leaveData.worker_id} onChange={(e) => setLeaveData({...leaveData, worker_id: parseInt(e.target.value)})} required>
                        <option value="">Select Worker</option>
                        {(state.workers || []).map(worker => (
                          <option key={worker.id} value={worker.id}>{worker.name}</option>
                        ))}
                      </select>   
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold">Leave Type *</label>
                      <select className="form-select" value={leaveData.type} onChange={(e) => setLeaveData({...leaveData, type: e.target.value})} required>
                        <option value="">Select Type</option>
                        {(state.leaveTypes || []).map(leaveType => (
                          <option key={leaveType.id} value={leaveType.leaveName}>{leaveType.leaveName}</option>
                        ))}
                      </select>
                      {leaveData.worker_id && leaveData.type && (() => {
                        const selectedLeaveType = (state.leaveTypes || []).find(lt => lt.leaveName === leaveData.type);
                        if (selectedLeaveType) {
                          let maxDays;
                          
                          if (selectedLeaveType.byExperience) {
                            const experienceCheck = checkWorkerExperience(leaveData.worker_id, selectedLeaveType);
                            if (selectedLeaveType.experienceRanges && selectedLeaveType.experienceRanges.length > 0) {
                              // Find the appropriate range for worker's experience
                              const worker = (state.workers || []).find(w => w.id === leaveData.worker_id);
                              if (worker) {
                                const joinDate = new Date(worker.joinDate || worker.joinDt);
                                const experienceYears = (new Date() - joinDate) / (1000 * 60 * 60 * 24 * 365.25);
                                
                                const applicableRange = selectedLeaveType.experienceRanges.find(range => {
                                  const fromYears = range.experienceFrom || 0;
                                  const toYears = range.experienceTo;
                                  return experienceYears >= fromYears && (toYears ? experienceYears <= toYears : true);
                                });
                                
                                maxDays = applicableRange ? applicableRange.experienceMaxPerYear : 0;
                              } else {
                                maxDays = selectedLeaveType.experienceRanges[0]?.experienceMaxPerYear || 0;
                              }
                            } else {
                              maxDays = selectedLeaveType.experienceMaxPerYear || 0;
                            }
                          } else {
                            maxDays = selectedLeaveType.maxPerYear || 30;
                          }
                          
                          const currentTypeLeaves = getLeaveTypeBreakdown(leaveData.worker_id)[leaveData.type] || 0;
                          const remainingDays = Math.max(0, maxDays - currentTypeLeaves);
                          return (
                            <div>
                              <small className={`mt-1 d-block ${remainingDays === 0 ? 'text-danger' : remainingDays <= 2 ? 'text-warning' : 'text-info'}`}>
                                Used: {currentTypeLeaves}/{maxDays} days | Remaining: {remainingDays} days
                              </small>
                              <small className={`d-block ${selectedLeaveType.paidUnpaid === 'Paid' ? 'text-success' : 'text-warning'}`}>
                                Type: {selectedLeaveType.paidUnpaid} Leave
                              </small>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold">Session *</label>
                      <select className="form-select" value={leaveData.session} onChange={(e) => setLeaveData({...leaveData, session: e.target.value})} required>
                        <option value="">Select Session</option>
                        <option value="Full Day">Full Day</option>
                        <option value="First Half">First Half</option>
                        <option value="Second Half">Second Half</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold">From Date *</label>
                      <DatePicker
                        value={leaveData.startDate}
                        onChange={(date) => setLeaveData({...leaveData, startDate: date})}
                        placeholder="Select start date"
                        minDate={new Date().toISOString().split('T')[0]}
                        required
                      />
                      {!leaveData.startDate && <div className="invalid-feedback d-block">From date is required</div>}
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-bold">To Date</label>
                      <DatePicker
                        value={leaveData.endDate}
                        onChange={(date) => setLeaveData({...leaveData, endDate: date})}
                        placeholder="Select end date"
                        minDate={leaveData.startDate || new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label fw-bold">Reason/Remark *</label>
                      <textarea className="form-control" rows="2" value={leaveData.reason} onChange={(e) => setLeaveData({...leaveData, reason: e.target.value})} required></textarea>
                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light">
                  <button type="submit" className="btn btn-success px-4">Save</button>
                  <button type="button" className="btn btn-outline-secondary" onClick={() => {
                    setLeaveData({ worker_id: '', type: '', startDate: '', endDate: '', session: '', reason: '' });
                    setShowModal(false);
                  }}>Close</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h5>Leave Requests</h5>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Worker</th>
                  <th>Type</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Days</th>
                  <th>Total Previous Leave</th>
                  <th>Session</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(state.leaves || []).map(leave => (
                  <tr key={leave.id}>
                    <td>{getWorkerName(leave)}</td>
                    <td>
                      {leave.type}
                      {(() => {
                        const selectedLeaveType = (state.leaveTypes || []).find(lt => lt.leaveName === leave.type);
                        if (selectedLeaveType) {
                          return (
                            <div>
                              <small className={`badge ${selectedLeaveType.paidUnpaid === 'Paid' ? 'bg-success' : 'bg-warning'} ms-1`}>
                                {selectedLeaveType.paidUnpaid}
                              </small>
                            </div>
                          );
                        }
                        return null;
                      })()} 
                    </td>
                    <td>{leave.startDt}</td>
                    <td>{leave.endDt}</td>
                    <td>{leave.session === 'First Half' || leave.session === 'Second Half' ? '0.5' : calculateDays(leave.startDt, leave.endDt)}</td>
                    <td>
                      <div className="d-flex flex-column gap-1">
                        <span className="badge bg-info">{getTotalPreviousLeaves(leave.workerId)} days</span>
                        <small className="text-muted">{formatLeaveBreakdown(leave.workerId)}</small>
                        {(() => {
                          const selectedLeaveType = (state.leaveTypes || []).find(lt => lt.leaveName === leave.type);
                          if (selectedLeaveType) {
                            const experienceCheck = checkWorkerExperience(leave.workerId, selectedLeaveType);
                            const maxDays = experienceCheck.maxPerYear || selectedLeaveType.maxPerYear || 30;
                            const currentTypeLeaves = getLeaveTypeBreakdown(leave.workerId)[leave.type] || 0;
                            const leaveDays = calculateDays(leave.startDt, leave.endDt);
                            // Only show warning if this leave is approved and would exceed limit
                            if (leave.leaveStatus === 1 && currentTypeLeaves > maxDays) {
                              return <small className="text-danger">⚠️ Exceeds limit by {currentTypeLeaves - maxDays} days</small>;
                            }
                          }
                          return null;
                        })()}
                      </div>
                    </td>
                    <td>{leave.session}</td>
                    <td>{leave.reason}</td>
                    <td>
                      <span className={`badge ${
                        leave.leaveStatus === 1 ? 'bg-success' :
                        leave.leaveStatus === 2 ? 'bg-danger' : 'bg-warning'
                      }`}>
                        {leave.leaveStatus === 1 ? 'Approved' : 
                         leave.leaveStatus === 2 ? 'Rejected' : 'Pending'}
                      </span>
                    </td>
                    <td>
                      <div className="btn-group">
                        <PermissionGuard module={MODULES.LEAVE} action={ACTIONS.INFO}>
                        <button 
                        title='View Details '
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            setSelectedLeave(leave);
                            setShowInfoModal(true);
                          }}style={{ fontSize: '13px', padding: '4px 10px' }}
                        >
                          <i class="bi bi-info-circle-fill"></i>
                        </button>
                        </PermissionGuard>
                        {leave.leaveStatus === 0 && (
                          <>
                          <PermissionGuard module={MODULES.LEAVE} action={ACTIONS.APPROVE}>
                            <button 
                            title='Approve'
                              className="btn btn-sm btn-primary"
                              onClick={() => handleStatusChange(leave.id, 'Approved')}style={{ fontSize: '13px', padding: '4px 10px' }}
                            >
                              <i class="bi bi-patch-check-fill"></i>
                            </button>
                          </PermissionGuard>
                          
                          <PermissionGuard module={MODULES.LEAVE} action={ACTIONS.REJECT}>
                            <button 
                            title='Cancel'
                              className="btn btn-sm btn-danger"
                              onClick={() => handleStatusChange(leave.id, 'Rejected')}style={{ fontSize: '13px', padding: '4px 10px' }}
                            >
                              <i class="bi bi-x-circle-fill"></i>
                            </button>
                            </PermissionGuard>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showInfoModal && selectedLeave && (
        <div className="modal" style={{display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow">
              <div className="modal-header bg-info text-white">
                <h5 className="modal-title">Leave Details</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowInfoModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="row g-3">
                  <div className="col-6">
                    <strong>Worker:</strong> {getWorkerName(selectedLeave)}
                  </div>
                  <div className="col-6">
                    <strong>Leave Type:</strong> {selectedLeave.type}
                    {(() => {
                      const selectedLeaveType = (state.leaveTypes || []).find(lt => lt.leaveName === selectedLeave.type);
                      if (selectedLeaveType) {
                        return (
                          <span className={`badge ${selectedLeaveType.paidUnpaid === 'Paid' ? 'bg-success' : 'bg-warning'} ms-2`}>
                            {selectedLeaveType.paidUnpaid}
                          </span>
                        );
                      }
                      return null;
                    })()} 
                  </div>
                  <div className="col-6">
                    <strong>Start Date:</strong> {selectedLeave.startDt}
                  </div>
                  <div className="col-6">
                    <strong>End Date:</strong> {selectedLeave.endDt}
                  </div>
                  <div className="col-6">
                    <strong>Days:</strong> {calculateDays(selectedLeave.startDt, selectedLeave.endDt)}
                  </div>
                  <div className="col-12">
                    <strong>Total Previous Leave:</strong> <span className="badge bg-info">{getTotalPreviousLeaves(selectedLeave.workerId)} days</span>
                    <div className="mt-2">
                      <strong>Leave Type Breakdown:</strong>
                      <div className="mt-1">
                        {Object.entries(getLeaveTypeBreakdown(selectedLeave.workerId)).length > 0 ? (
                          Object.entries(getLeaveTypeBreakdown(selectedLeave.workerId)).map(([type, days]) => (
                            <span key={type} className="badge bg-secondary me-1 mb-1">{type}: {days} days</span>
                          ))
                        ) : (
                          <span className="text-muted">No previous leaves</span>
                        )}
                      </div>
                    </div>
                    {(() => {
                      const selectedLeaveType = (state.leaveTypes || []).find(lt => lt.leaveName === selectedLeave.type);
                      if (selectedLeaveType) {
                        const experienceCheck = checkWorkerExperience(selectedLeave.workerId, selectedLeaveType);
                        return (
                          <div className="mt-2">
                            <strong>Experience Eligibility:</strong>
                            <div className={`mt-1 p-2 rounded ${experienceCheck.eligible ? 'bg-success bg-opacity-10 text-success' : 'bg-warning bg-opacity-10 text-warning'}`}>
                              {experienceCheck.eligible ? (
                                <span>✅ Eligible - Worker has {experienceCheck.experienceYears} years experience</span>
                              ) : (
                                <span>⚠️ Not Eligible - Worker has {experienceCheck.experienceYears} years but requires {experienceCheck.requiredRange}</span>
                              )}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  <div className="col-6">
                    <strong>Session:</strong> {selectedLeave.session}
                  </div>
                  <div className="col-12">
                    <strong>Status:</strong> 
                    <span className={`badge ms-2 ${
                      selectedLeave.leaveStatus === 1 ? 'bg-success' :
                      selectedLeave.leaveStatus === 2 ? 'bg-danger' : 'bg-warning'
                    }`}>
                      {selectedLeave.leaveStatus === 1 ? 'Approved' : 
                       selectedLeave.leaveStatus === 2 ? 'Rejected' : 'Pending'}
                    </span>
                  </div>
                  <div className="col-12">
                    <strong>Reason:</strong>
                    <p className="mt-2 p-2 bg-light rounded">{selectedLeave.reason}</p>
                  </div>
                </div>
              </div>
              <div className="modal-footer bg-light">
                {selectedLeave.leaveStatus === 0 && (
                  <div className="btn-group me-2">
                    <button 
                      className="btn btn-success"
                      onClick={() => {
                        handleStatusChange(selectedLeave.id, 'Approved');
                        setShowInfoModal(false);
                      }}
                    >
                      Approve
                    </button>
                    <button 
                      className="btn btn-danger"
                      onClick={() => {
                        handleStatusChange(selectedLeave.id, 'Rejected');
                        setShowInfoModal(false);
                      }}
                    >
                      Reject
                    </button>
                  </div>
                )}
                <button type="button" className="btn btn-outline-secondary" onClick={() => {
                  setShowInfoModal(false);
                }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </PermissionGuard>
  );
}

export default Leave;